import React from "react";
import { Link } from "react-router-dom";
import { Box, Drawer, IconButton, List, ListItem, ListItemText, Tooltip } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import AuthContext from "../Context/AuthProvider";
import IntroModal from "../acknowledgements/IntroCarouselModal";
import PaymentModal from "./PaymentModal";
import GradientLogo from '../../assets/images/GradientLogo.png';

const Navigationbar = () => {
  const { getToken, logout, profile, userRole, isUserPaymentValid } = React.useContext(AuthContext);
  const isAdmin = userRole === "admin";
  const auth = getToken();
  const [isOpen, setIsOpen] = React.useState(false);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [openIntroModal, setOpenIntroModal] = React.useState(false);
  const [showPaymentModal, setShowPaymentModal] = React.useState(false);
  const handleOpenIntroModal = () => setOpenIntroModal(true);
  const handleCloseIntroModal = () => setOpenIntroModal(false);

  const toggleDrawer = (open) => (event) => {
    if (event.type === "keydown" && (event.key === "Tab" || event.key === "Shift")) return;
    setDrawerOpen(open);
  };

  const handleModal = () => setIsOpen((prev) => !prev);

  const handleBackdropClick = (e) => {
    if (e.target.id === "backdrop") setIsOpen(false);
  };

  const menuItems = [
    { label: "Home", path: "/", show: auth?.token },
    { label: "Admin Home", path: "/administratorpath/home", show: isAdmin },
    { label: "View Reports", path: "/administratorpath/reports", show: isAdmin },
    { label: "Create Account", path: "/administratorpath/createprofile", show: isAdmin },
    { label: "Update Password", path: "/administratorpath/passwordmanagement", show: isAdmin },
    { label: "About", path: "/about", show: !isAdmin },
    { label: "My Profile", path: "/myprofile", show: auth?.token && !isAdmin },
    { label: "Success Stories", path: "/successStories", show: !isAdmin },
    { label: "Contact Us", path: "#", show: true, onClick: handleModal },
    { label: "Getting Started", path: "#", show: !profile.firstName || isAdmin, onClick: handleOpenIntroModal },
    { label: "Previewed Profiles", path: "/previewed-profiles", show: auth?.token && !isAdmin && !isUserPaymentValid },
    { label: "My Favourites", path: "/favourites", show: profile.firstName && !isAdmin },
  ];

  const renderMenuItems = () =>
    menuItems
      .filter((item) => item.show)
      .map((item) => {
        if (item.label === "My Favourites" && !isUserPaymentValid) {
          return (
            <Tooltip 
              key={item.label}
              title="Unlock favourites feature with annual subscription"
              placement="right"
            >
              <ListItem button onClick={() => setShowPaymentModal(true)}>
                <ListItemText primary={item.label} />
              </ListItem>
            </Tooltip>
          );
        }
        return (
          <ListItem
            button
            component={item.path !== "#" ? Link : "div"}
            to={item.path !== "#" ? item.path : undefined}
            onClick={item.onClick}
            key={item.label}
          >
            <ListItemText primary={item.label} />
          </ListItem>
        );
      });

  const renderActionButtons = () =>
    auth?.token ? (
      <ListItem button onClick={logout}>
        <ListItemText primary="Logout" />
      </ListItem>
    ) : (
      <ListItem button component={Link} to="/login">
        <ListItemText primary="Login" />
      </ListItem>
    );

  const renderDesktopLinks = () =>
    menuItems
      .filter((item) => item.show)
      .map((item) => {
        if (item.label === "My Favourites" && !isUserPaymentValid) {
          return (
            <Tooltip 
              key={item.label}
              title="Unlock favourites feature with annual subscription"
              placement="bottom"
            >
              <span
                className="no-underline text-gray-400 cursor-pointer text-md font-semibold"
                onClick={() => setShowPaymentModal(true)}
              >
                {item.label}
              </span>
            </Tooltip>
          );
        }
        return (
          <span key={item.label}>
            {item.path !== "#" ? (
              <Link
                to={item.path}
                className="no-underline text-gray-800 hover:text-gray-900 text-md font-semibold"
              >
                {item.label}
              </Link>
            ) : (
              <span
                onClick={item.onClick}
                className="no-underline text-gray-800 hover:text-gray-900 text-md font-semibold cursor-pointer"
              >
                {item.label}
              </span>
            )}
          </span>
        );
      });

  return (
    <div className="navbar w-full bg-white px-2 sm:px-4 py-3 fixed">
      <div className="flex-1 flex items-center gap-2 flex-wrap">
        <a href="/" className="flex items-center no-underline text-black">
          <img src={GradientLogo} alt="GSB Soirika Logo" style={{ height: '40px', maxWidth: '100%', width: 'auto', marginRight: '8px' }} />
          <span className="text-2xl font-bold">GSB Soirika</span>
        </a>
      </div>
      <div className="flex-none">
        <IconButton
          sx={{ display: { lg: "none" } }}
          edge="start"
          color="inherit"
          aria-label="menu"
          onClick={toggleDrawer(true)}
        >
          <MenuIcon />
        </IconButton>
        <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer(false)}>
          <List onClick={toggleDrawer(false)} onKeyDown={toggleDrawer(false)}>
            {renderMenuItems()}
            {renderActionButtons()}
          </List>
        </Drawer>
        <Box sx={{ display: { xs: "none", lg: "block" } }}>
          <div className="flex items-center justify-center gap-4 p-0">
            {renderDesktopLinks()}
            {auth?.token ? (
              <button
                onClick={logout}
                className="no-underline text-md font-semibold bg-primary text-white rounded-md flex items-center justify-center leading-1 px-6 py-2 hover:bg-blue-900"
              >
                Log Out
              </button>
            ) : (
              <Link
                to="/login"
                className="no-underline text-md font-semibold bg-primary text-white rounded-md flex items-center justify-center leading-1 px-6 py-2 hover:bg-blue-900"
              >
                Login
              </Link>
            )}
          </div>
        </Box>
      </div>
      <IntroModal open={openIntroModal} handleClose={handleCloseIntroModal} />

      {isOpen && (
        <div
          id="backdrop"
          className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50"
          onClick={handleBackdropClick}
        >
          <div className="bg-white p-8 rounded-lg shadow-lg w-96 flex flex-col items-center justify-center text-center">
            <div className="flex items-center justify-center w-full">
              <h2 className="text-lg sm:text-2xl font-bold text-gray-800 text-center">Contact Us</h2>
            </div>
            <p className="mt-1 text-gray-700 font-medium">
              For any assistance, please contact us at<br /> +91 8296919479.
            </p>
            <button className="mt-2 bg-blue-700 text-white py-2 px-6 rounded hover:bg-blue-800" onClick={handleModal}>
              Close
            </button>
          </div>
        </div>
      )}
      <PaymentModal open={showPaymentModal} onClose={() => setShowPaymentModal(false)} />
    </div>
  );
};

export default Navigationbar;
